import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const ARIA_LIVE_MATCHER = /^\s*Tab\s+(\d+)\s+of\s+(\d+)\s*$/i;

const getTabKey = (element: Element | null): string | null => {
  const wrapper = element?.closest("[data-node-key]") as HTMLElement | null;
  return wrapper?.getAttribute("data-node-key") ?? null;
};

const getEnabledTabs = (tablist: HTMLElement): HTMLElement[] =>
  Array.from(tablist.querySelectorAll<HTMLElement>('[role="tab"]')).filter(
    (tab) => tab.getAttribute("aria-disabled") !== "true"
  );

const collectTabIds = (tablist: HTMLElement): string[] => {
  const ids: string[] = [];
  tablist.querySelectorAll<HTMLElement>('[role="tab"]').forEach((tab) => {
    if (tab.id) ids.push(tab.id);
  });
  return ids;
};

const setTablistAriaOwns = (tablist: HTMLElement) => {
  const ids = collectTabIds(tablist);
  if (ids.length === 0) return;
  const nextOwns = ids.join(" ");
  if (tablist.getAttribute("aria-owns") !== nextOwns) {
    tablist.setAttribute("aria-owns", nextOwns);
  }
};

const linkAllTablistsToTabs = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>('[role="tablist"]').forEach(setTablistAriaOwns);
};

const getNextKey = (
  tablist: HTMLElement,
  currentKey: string | null,
  event: KeyboardEvent
): string | null => {
  const orientation = tablist.getAttribute("aria-orientation") ?? "horizontal";
  const isHorizontal = orientation !== "vertical";
  const prevKey = isHorizontal ? "ArrowLeft" : "ArrowUp";
  const nextKey = isHorizontal ? "ArrowRight" : "ArrowDown";

  if (
    event.key !== prevKey &&
    event.key !== nextKey &&
    event.key !== "Home" &&
    event.key !== "End"
  ) {
    return null;
  }

  const tabs = getEnabledTabs(tablist);
  if (tabs.length === 0) return null;
  const currentIndex = tabs.findIndex((tab) => getTabKey(tab) === currentKey);
  const len = tabs.length;
  let nextIndex: number;

  switch (event.key) {
    case prevKey:
      nextIndex = (currentIndex < 0 ? 0 : currentIndex - 1 + len) % len;
      break;
    case nextKey:
      nextIndex = (currentIndex < 0 ? 0 : currentIndex + 1) % len;
      break;
    case "Home":
      nextIndex = 0;
      break;
    case "End":
      nextIndex = len - 1;
      break;
    default:
      return null;
  }

  return getTabKey(tabs[nextIndex]);
};

export const useTabsAutoActivate = <T extends HTMLElement = HTMLDivElement>(
  onActivate: (key: string) => void,
  activeKey?: string
) => {
  const containerRef = useRef<T>(null);
  const onActivateRef = useRef(onActivate);
  const activeKeyRef = useRef(activeKey);
  const { t } = useTranslation();
  const tRef = useRef(t);

  useEffect(() => {
    onActivateRef.current = onActivate;
  }, [onActivate]);

  useEffect(() => {
    activeKeyRef.current = activeKey;
  }, [activeKey]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const focusedTab = target?.closest('[role="tab"]') as HTMLElement | null;
      if (!focusedTab || !root.contains(focusedTab)) return;
      const tablist = focusedTab.closest('[role="tablist"]') as HTMLElement | null;
      if (!tablist) return;
      const fromKey = getTabKey(focusedTab) ?? activeKeyRef.current ?? null;
      const nextKey = getNextKey(tablist, fromKey, event);
      if (nextKey && nextKey !== activeKeyRef.current) {
        onActivateRef.current(nextKey);
      }
    };

    root.addEventListener("keydown", handleKeyDown, true);

    return () => {
      root.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const localize = (region: HTMLElement) => {
      const original = region.textContent ?? "";
      const match = original.match(ARIA_LIVE_MATCHER);
      if (!match) return;
      const current = Number(match[1]);
      const total = Number(match[2]);
      const localized = tRef.current("common.accessibility.tabs.position", {
        current,
        total,
      });
      if (localized && localized !== original) {
        region.textContent = localized;
      }
    };

    const localizeAll = (target: ParentNode) => {
      target.querySelectorAll<HTMLElement>('[role="tab"] [aria-live="polite"]').forEach(localize);
    };

    localizeAll(root);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;
            if (node.matches?.('[aria-live="polite"]')) {
              localize(node);
            }
            localizeAll(node);
          });
        } else if (mutation.type === "characterData") {
          const parent = mutation.target.parentElement;
          if (parent?.getAttribute("aria-live") === "polite") {
            localize(parent);
          }
        }
      }
    });

    observer.observe(root, { childList: true, subtree: true, characterData: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const linkTablistToTabs = () => linkAllTablistsToTabs(root);

    linkTablistToTabs();

    const observer = new MutationObserver(linkTablistToTabs);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributeFilter: ["role", "id"],
    });

    return () => observer.disconnect();
  }, []);

  return containerRef;
};
